import { useState, useEffect, type ReactNode, type FormEvent } from 'react';
import { useAuthStore } from '../../stores/auth';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.profitos.pro';

interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
}

interface ApiKeysResponse {
  success: boolean;
  data?: { keys: ApiKey[] };
  error?: { message?: string };
}

interface CreateKeyResponse {
  success: boolean;
  data?: { key: string; apiKey: ApiKey };
  error?: { message?: string };
}

const AVAILABLE_SCOPES = [
  { id: 'read:links', label: 'Read Links', description: 'View affiliate links' },
  { id: 'write:links', label: 'Write Links', description: 'Create and modify links' },
  { id: 'read:analytics', label: 'Read Analytics', description: 'View performance data' },
  { id: 'read:offers', label: 'Read Offers', description: 'View available offers' },
];

export function ApiKeysSettings(): ReactNode {
  const { accessToken } = useAuthStore();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>(['read:links', 'read:analytics']);
  const [isCreating, setIsCreating] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [revokeConfirmId, setRevokeConfirmId] = useState<string | null>(null);

  useEffect(() => {
    void fetchKeys();
  }, [accessToken]);

  const fetchKeys = async (): Promise<void> => {
    try {
      const res = await fetch(`${API_URL}/auth/api-keys`, {
        headers: {
          Authorization: `Bearer ${accessToken ?? ''}`,
        },
      });

      if (!res.ok) {
        // If endpoint doesn't exist yet, show empty state
        if (res.status === 404) {
          setKeys([]);
          setIsLoading(false);
          return;
        }
        throw new Error('Failed to fetch API keys');
      }

      const data = (await res.json()) as ApiKeysResponse;
      if (data.success && data.data !== undefined) {
        setKeys(data.data.keys);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateKey = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setIsCreating(true);

    try {
      const res = await fetch(`${API_URL}/auth/api-keys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken ?? ''}`,
        },
        body: JSON.stringify({
          name: newKeyName,
          scopes: selectedScopes,
        }),
      });

      if (!res.ok) {
        throw new Error('Failed to create API key');
      }

      const data = (await res.json()) as CreateKeyResponse;
      if (data.success && data.data !== undefined) {
        setNewKey(data.data.key);
        setKeys((prev) => [data.data!.apiKey, ...prev]);
        setNewKeyName('');
        setSelectedScopes(['read:links', 'read:analytics']);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create API key');
    } finally {
      setIsCreating(false);
    }
  };

  const handleRevokeKey = async (keyId: string): Promise<void> => {
    try {
      const res = await fetch(`${API_URL}/auth/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken ?? ''}`,
        },
      });

      if (!res.ok) {
        throw new Error('Failed to revoke API key');
      }

      setKeys((prev) => prev.filter((k) => k.id !== keyId));
      setRevokeConfirmId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke API key');
    }
  };

  const toggleScope = (scope: string): void => {
    setSelectedScopes((prev) =>
      prev.includes(scope)
        ? prev.filter((s) => s !== scope)
        : [...prev, scope]
    );
  };

  const formatDate = (dateStr: string | null): string => {
    if (dateStr === null) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="rounded-lg border bg-white p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-lg border bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">API Keys</h2>
            <p className="mt-1 text-sm text-gray-600">
              Manage API keys for programmatic access to your account
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            Create API Key
          </button>
        </div>

        {error !== null && (
          <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Keys List */}
        <div className="mt-6">
          {keys.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-gray-200 p-8 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No API keys yet</h3>
              <p className="mt-2 text-gray-500">
                Create your first API key to start integrating with ProfitOS
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {keys.map((key) => (
                <div key={key.id} className="py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{key.name}</p>
                      <p className="text-sm text-gray-500">
                        <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">
                          {key.keyPrefix}...
                        </code>
                        <span className="mx-2">·</span>
                        Created {formatDate(key.createdAt)}
                        {key.lastUsedAt !== null && (
                          <>
                            <span className="mx-2">·</span>
                            Last used {formatDate(key.lastUsedAt)}
                          </>
                        )}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {key.scopes.map((scope) => (
                          <span
                            key={scope}
                            className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
                          >
                            {scope}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      {revokeConfirmId === key.id ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => void handleRevokeKey(key.id)}
                            className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setRevokeConfirmId(null)}
                            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setRevokeConfirmId(key.id)}
                          className="text-sm font-medium text-red-600 hover:text-red-700"
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Documentation */}
      <div className="rounded-lg border bg-white p-6">
        <h3 className="text-lg font-semibold text-gray-900">Using API Keys</h3>
        <p className="mt-1 text-sm text-gray-600">
          Include your API key in the Authorization header of your requests
        </p>
        <div className="mt-4 rounded-lg bg-gray-900 p-4">
          <code className="text-sm text-green-400">
            curl -H &quot;Authorization: Bearer YOUR_API_KEY&quot; \<br />
            &nbsp;&nbsp;https://api.profitos.pro/affiliate/links
          </code>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Create API Key</h3>
            <p className="mt-1 text-sm text-gray-600">
              Generate a new API key with specific permissions
            </p>

            {newKey !== null ? (
              <div className="mt-6">
                <div className="rounded-lg bg-green-50 p-4">
                  <p className="text-sm font-medium text-green-800">
                    Your new API key has been created
                  </p>
                  <p className="mt-1 text-xs text-green-700">
                    Copy this key now. You won&apos;t be able to see it again.
                  </p>
                </div>
                <div className="mt-4">
                  <code className="block w-full rounded-lg bg-gray-100 p-3 font-mono text-sm break-all">
                    {newKey}
                  </code>
                </div>
                <button
                  onClick={() => {
                    setNewKey(null);
                    setShowCreateModal(false);
                  }}
                  className="mt-4 w-full rounded-lg bg-primary-600 py-2 text-sm font-medium text-white hover:bg-primary-700"
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={(e) => void handleCreateKey(e)} className="mt-6 space-y-4">
                <div>
                  <label
                    htmlFor="keyName"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Key Name
                  </label>
                  <input
                    type="text"
                    id="keyName"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-primary-500 focus:ring-primary-500"
                    placeholder="e.g., Production API Key"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Permissions
                  </label>
                  <div className="mt-2 space-y-2">
                    {AVAILABLE_SCOPES.map((scope) => (
                      <label
                        key={scope.id}
                        className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={selectedScopes.includes(scope.id)}
                          onChange={() => toggleScope(scope.id)}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {scope.label}
                          </p>
                          <p className="text-xs text-gray-500">{scope.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 rounded-lg border border-gray-300 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isCreating || newKeyName.trim() === '' || selectedScopes.length === 0}
                    className="flex-1 rounded-lg bg-primary-600 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    {isCreating ? 'Creating...' : 'Create Key'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
